import { Component, Output, EventEmitter, ViewEncapsulation, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { CacheService } from 'projects/common/src/app/services/cache.service';
import { ParameterHandlerService } from 'projects/common/src/app/services/parameter-handler.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { WithoutTimePresentationPipe } from 'src/app/modules/core/pipes/without-time-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IMappedTotalChargeTable, ITotalChargeResponse } from 'src/app/modules/widget/interfaces/total-charge';
import { CommunicationService } from 'src/app/services/communication/communication.service';
import { DashboardWidgetBasicComponent } from '../../dashboard-widget-basic/dashboard-widget-basic.component';
import { TotalChargeModalComponent } from './total-charge-modal/total-charge-modal.component';
import { fixTimePresentation } from 'projects/common/src/app/services/utils/utilities';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { TotalExtractionService } from '../../../services/total-extraction/total-extraction.service';

@Component({
  selector: 'app-total-charge',
  templateUrl: './total-charge.component.html',
  styleUrls: ['./total-charge.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TotalChargeComponent extends DashboardWidgetBasicComponent implements OnDestroy {
  @Output()
  public columnSelected: EventEmitter<any>;

  public tableConfig: ITableConfig;
  public dataTotalCharge: ITotalChargeResponse;
  public shiftId: string;

  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private tableService: TableConfigService,
    private modalService: NgbModal,
    private lenguageService: LanguageService,
    private dateFormatPipe: WithoutTimePresentationPipe,
    protected screenViewService: ScreenViewService,
    private totalExtractionSvc: TotalExtractionService,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.columnSelected = new EventEmitter<any>();
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  private initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * buildView
   */
  public buildView(value: any): void {
    super.buildView(value);
    this.getChargeStatus(value);
  }

  /**
   * Get total Charge
   */
  public async getChargeStatus(dataTotalCharge: ITotalChargeResponse): Promise<any> {
    this.dataTotalCharge = dataTotalCharge;
    const allZeros = this.dataTotalCharge.productionPeriod.every((item) => item.productionInStage === 0);

    if (allZeros) {
      this.chartWithoutData$.next(true);
    } else this.chartWithoutData$.next(false);

    this.setTableDetailConfig(this.totalChargeMapper(dataTotalCharge));
  }

  /**
   * Mapper data total charge
   */
  private totalChargeMapper(response: ITotalChargeResponse): IMappedTotalChargeTable[] {
    if (response.shiftPeriodData) {
      const mappedTotalCharge = response.shiftData.map((shift) => {
        const production = response.productionPeriod.filter((prod) => prod.shiftId === shift.shiftId);

        let dataChargeRow: any = {
          shiftName: shift.name,
          firstProduction: this.dateFormatPipe.transform(shift.firstProduction),
        };

        dataChargeRow = production.reduce((acc, curr) => {
          const period = response.shiftPeriodData.find((prod) => prod.shiftPeriodId === curr.shiftPeriodId);

          return { ...acc, [period.name.toLowerCase().replace(/(\s)/g, '-')]: curr.productionInStage };
        }, dataChargeRow);

        dataChargeRow = {
          ...dataChargeRow,
          lastProduction: this.dateFormatPipe.transform(shift.lastProduction),
        };

        return dataChargeRow;
      });

      return mappedTotalCharge;
    }
  }

  /**
   * Set TableConfig totalcharge
   */
  private setTableDetailConfig(tableData: any[]): void {
    const headerParams: ITableHeaderParams = {
      data: tableData,
      excludeColumns: ['traslape'],
    };

    const datasetParams: ITableDataParams = {
      tableData,
      colorConfig: [
        { field: 'firstProduction', color: 'demora' },
        { field: 'lastProduction', color: 'demora' },
      ],
      clickConfig: [
        { field: 'firstProduction', hasCursorPointer: false, hasBoldHover: false },
        { field: 'lastProduction', hasCursorPointer: false, hasBoldHover: false },
      ],
      dateConfig: [{ field: 'shiftStartProd', format: 'full-date-time' }],
      excludeColumns: ['traslape'],
    };

    const extraParams: ITableExtraParams = {
      quantityPerPage: tableData.length,
      disableFooter: true,
    };

    const header = this.tableService.setHeaderConfig(headerParams);
    const dataset = this.tableService.setDatasetConfig(datasetParams);

    this.tableConfig = null;
    setTimeout(() => (this.tableConfig = { header, dataset, extraParams }), 0);
  }

  /**
   * Open modal with detail firstLoop or LastLoop
   */
  public openModalTotalCharge(data: any): void {
    if (this.dataTotalCharge) {
      const componentRef = this.modalService.open(TotalChargeModalComponent, { centered: true, windowClass: 'modal-change' });
      componentRef.componentInstance.title = data.title;
      componentRef.componentInstance.subtitle = data.subtitle;
      componentRef.componentInstance.shift = data.shift;
      componentRef.componentInstance.data = data.data;
    }
  }

  /**
   * Build subtitle modal with date and hour shift
   */
  private buildSinceShift(shiftId: string, inputDate: string): string {
    const shiftCollection = this.parameterService.parameters.item('shift-collection').value as Array<any>;
    const shiftObj = shiftCollection.find((shift) => shift.name === shiftId);
    const startTime = `${fixTimePresentation(shiftObj.startTime.hours)} : ${fixTimePresentation(shiftObj.startTime.minutes)}`;
    const endTime = `${fixTimePresentation(shiftObj.endTime.hours)} : ${fixTimePresentation(shiftObj.endTime.minutes)}`;
    const dateArray = inputDate.split('-');
    const fomattedDate = `${dateArray[2]}/${dateArray[1]}/${dateArray[0]}`;

    return `${fomattedDate} de ${startTime} a ${endTime}`;
  }

  /**
   * Click colum with open modal
   */
  public async clickColum(e): Promise<void> {
    const isFirstProduction = e.cell.key === 'firstProduction';
    const isLastProduction = e.cell.key === 'lastProduction';
    const startShift = e.row.find((data) => data.key === 'inicio-de-turno');
    const endShift = e.row.find((data) => data.key === 'fin-de-turno');

    if (
      e.row.length &&
      ((startShift && startShift.value > 0 && isFirstProduction) || (endShift && endShift.value > 0 && isLastProduction))
    ) {
      const shiftCollection = this.parameterService.parameters.item('shift-collection').value as Array<any>;
      const shiftObj = shiftCollection.find((shift) => shift.name === e.row[0].value);
      const inputDate = String(this.parameterService.parameters.item('date').value);

      if (isFirstProduction) {
        this.totalExtractionSvc.getFirstLoopDetailByShiftCompany(inputDate, parseFloat(shiftObj.shiftId), 'ShiftStart').subscribe({
          next: (resp) => {
            if (resp) {
              const dataMoDal = {
                title: this.lenguageService.message(isFirstProduction ? 'firstloops' : 'lastloops'),
                subtitle: this.buildSinceShift(e.row[0].value, inputDate),
                shift: e.row[0].value,
                data: resp,
              };
              this.openModalTotalCharge(dataMoDal);
            }
          },
          error: (err) => console.error(err),
        });
      } else {
        this.totalExtractionSvc.getLastLoopDetailByShiftCompany(inputDate, parseFloat(shiftObj.shiftId), 'ShiftEnd').subscribe({
          next: (resp) => {
            if (resp) {
              const dataMoDal = {
                title: this.lenguageService.message(isFirstProduction ? 'firstloops' : 'lastloops'),
                subtitle: this.buildSinceShift(e.row[0].value, inputDate),
                shift: e.row[0].value,
                data: resp,
              };
              this.openModalTotalCharge(dataMoDal);
            }
          },
          error: (err) => console.error(err),
        });
      }
    }
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
